import { FC, useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { IoClose } from 'react-icons/io5'
import { MdOpenInNew, MdErrorOutline } from 'react-icons/md'
import {
  Container,
  ModalHeader,
  HeaderActions,
  HelperText,
  OpenInNewTabLink,
  CloseButton,
  InvoiceCloudFrame,
  LoaderContainer,
  LoaderMessage,
  ReadyContainer,
  PrimaryLinkButton,
  ErrorContainer,
  ErrorActions,
  ContactLink
} from './styles'
import { OrangeButton } from '../index'
import loaderImg from 'src/assets/loader.svg'
import { api } from 'src/services/apiClient'
import { useAuth } from 'src/context/auth'
import { tlieSources } from 'src/constants/tlie'
import { PAYMENT_WINDOW_MODE } from './paymentWindowMode'

interface Props {
  close?: () => void;
}

type Status = 'loading' | 'ready' | 'error'

// A retry only helps when the failure was transient. A firm with no InvoiceCloud
// payment data (404/406) fails identically on every attempt, so offering
// "Try again" there just delays the phone call that will actually fix it.
type ErrorKind = 'transient' | 'permanent'

/**
 * Only used by the 'iframe' mode, which InvoiceCloud's X-Frame-Options blocks -
 * read ./paymentWindowMode.ts before relying on any of this.
 *
 * The checkout submits forms, opens its own 3-D Secure / bank windows, uses
 * window.confirm during checkout and offers a receipt download. Without these
 * tokens the browser silently blocks the submit and the customer cannot pay -
 * that was the original TLIE-1117 defect, sitting behind the framing one.
 *
 * `allow-top-navigation` (unqualified) is deliberately NOT granted: gated on
 * user activation, InvoiceCloud can only navigate the portal away as a result
 * of a click the customer just made, never silently.
 */
const IFRAME_SANDBOX = [
  'allow-same-origin',
  'allow-scripts',
  'allow-forms',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-top-navigation-by-user-activation',
  'allow-modals',
  'allow-downloads'
].join(' ')

// The shared axios client sets no timeout, so a request that never settles
// would sit on the spinner forever - the exact dead end this screen is fixing.
const REQUEST_TIMEOUT_MS = 20000

/**
 * The endpoint answers with an empty body (ASP.NET turns `Ok(null)` into a 204)
 * when InvoiceCloud returns no RemoteAuthenticationToken. Guard the scheme so a
 * non-URL body can never reach an iframe `src` or an `<a href>`; the value comes
 * from our own authenticated API, not from user input.
 */
const toSafeUrl = (value: unknown): string => {
  if (typeof value !== 'string') {
    return ''
  }

  const trimmed = value.trim()

  if (!trimmed) {
    return ''
  }

  try {
    return new URL(trimmed).protocol === 'https:' ? trimmed : ''
  } catch {
    return ''
  }
}

/**
 * The backend always mints the URL for an iframe - `ViewMode=IFrame`
 * (InvoiceCloudService.cs:250-254). Opened as a full page that would render
 * InvoiceCloud's chrome-less embedded skin, so ask for the standalone
 * presentation instead. Normalising here rather than in the API is what lets
 * PAYMENT_WINDOW_MODE be flipped without touching the backend.
 */
const toNewTabUrl = (frameUrl: string): string => {
  try {
    const url = new URL(frameUrl)
    url.searchParams.set('ViewMode', '0')
    return url.href
  } catch {
    return frameUrl
  }
}

const errorKindFor = (err: unknown): ErrorKind => {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined

  // 404 "firm payment data not found" / 406 "No administrator details found"
  return status === 404 || status === 406 ? 'permanent' : 'transient'
}

const InvoiceCloudModalContent: FC<Props> = ({ close }) => {
  const { user } = useAuth()
  const [invoiceCloudUrl, setInvoiceCloudUrl] = useState('')
  const [status, setStatus] = useState<Status>('loading')
  const [errorKind, setErrorKind] = useState<ErrorKind>('transient')
  const [attempt, setAttempt] = useState(0)

  const firmId = user?.firmId

  // Keyed only on values this effect never writes, so a failure can never
  // re-arm the request. Retrying is explicit: it bumps `attempt`.
  useEffect(() => {
    let cancelled = false

    const loadInvoiceCloudUrl = async () => {
      setStatus('loading')
      setInvoiceCloudUrl('')

      if (!firmId) {
        console.error('[InvoiceCloud] no firmId on the signed-in user; cannot open the payment portal')

        if (!cancelled) {
          setErrorKind('permanent')
          setStatus('error')
        }
        return
      }

      try {
        const { data } = await api.get('Payments/RemoteAuthenticationUrlForFirm', {
          params: { firmId },
          timeout: REQUEST_TIMEOUT_MS
        })

        if (cancelled) {
          return
        }

        const safeUrl = toSafeUrl(data)

        if (!safeUrl) {
          console.error('[InvoiceCloud] RemoteAuthenticationUrlForFirm returned no usable URL', data)
          setErrorKind('transient')
          setStatus('error')
          return
        }

        setInvoiceCloudUrl(safeUrl)
        setStatus('ready')
      } catch (err) {
        if (cancelled) {
          return
        }

        console.error('[InvoiceCloud] RemoteAuthenticationUrlForFirm request failed', err)
        setErrorKind(errorKindFor(err))
        setStatus('error')
      }
    }

    loadInvoiceCloudUrl()

    return () => {
      cancelled = true
    }
  }, [firmId, attempt])

  const retry = useCallback(() => setAttempt(current => current + 1), [])

  const framed = PAYMENT_WINDOW_MODE === 'iframe'

  return (
    <Container $compact={!framed}>
      <ModalHeader>
        <HeaderActions>
          {/* In 'new-tab' mode the primary action below already is this link,
              so the header hatch would only duplicate it. */}
          {framed && status === 'ready' ? (
            <>
              <HelperText>Having trouble?</HelperText>
              <OpenInNewTabLink
                href={toNewTabUrl(invoiceCloudUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in a new tab
                <MdOpenInNew aria-hidden="true" />
              </OpenInNewTabLink>
            </>
          ) : null}
        </HeaderActions>
        <CloseButton type="button" onClick={close} aria-label="Close payment window">
          <IoClose aria-hidden="true" />
        </CloseButton>
      </ModalHeader>

      {status === 'loading' ? (
        <LoaderContainer role="status">
          <img src={loaderImg} alt="" />
          <LoaderMessage>Connecting you to our secure payment portal&hellip;</LoaderMessage>
        </LoaderContainer>
      ) : null}

      {status === 'ready' && framed ? (
        <InvoiceCloudFrame
          title="InvoiceCloud payment portal"
          src={invoiceCloudUrl}
          sandbox={IFRAME_SANDBOX}
        />
      ) : null}

      {status === 'ready' && !framed ? (
        // A real anchor, not window.open: the URL only exists after an await,
        // and a tab opened after one is eaten by the popup blocker. The anchor
        // also gets noopener/noreferrer, keyboard access and cmd/middle-click.
        <ReadyContainer>
          <h3>Your payment portal is ready</h3>
          <p>
            You&apos;ll finish your payment on InvoiceCloud&apos;s secure site,
            which opens in a new tab. This page stays open behind it.
          </p>
          <PrimaryLinkButton
            href={toNewTabUrl(invoiceCloudUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open payment portal
            <MdOpenInNew aria-hidden="true" />
          </PrimaryLinkButton>
        </ReadyContainer>
      ) : null}

      {status === 'error' ? (
        <ErrorContainer role="alert">
          <MdErrorOutline aria-hidden="true" />
          <h3>We couldn&apos;t open the payment portal</h3>
          <p>
            Your payment hasn&apos;t been submitted and nothing has been charged.{' '}
            {errorKind === 'transient' ? 'Please try again, or call' : 'Please call'} TLIE at{' '}
            <ContactLink href={`tel:${tlieSources.phone}`}>{tlieSources.formattedPhone}</ContactLink>
            {' '}and we can take your payment over the phone.
          </p>
          {errorKind === 'transient' ? (
            <ErrorActions>
              <OrangeButton type="button" onClick={retry}>Try again</OrangeButton>
            </ErrorActions>
          ) : null}
        </ErrorContainer>
      ) : null}
    </Container>
  )
}

export default InvoiceCloudModalContent
